import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type UiLocale = "en" | "he";

const STORAGE_KEY = "mysipur_ui_locale";

function readStoredLocale(): UiLocale {
  if (typeof window === "undefined") return "en";
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "he" || v === "en") return v;
  } catch {
    /* ignore */
  }
  return "en";
}

function applyDocumentLocale(locale: UiLocale) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = locale === "he" ? "he" : "en";
  document.documentElement.dir = locale === "he" ? "rtl" : "ltr";
}

type UiLocaleContextValue = {
  uiLocale: UiLocale;
  setUiLocale: (locale: UiLocale) => void;
};

const UiLocaleContext = createContext<UiLocaleContextValue | null>(null);

export const UiLocaleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [uiLocale, setUiLocaleState] = useState<UiLocale>(() => {
    const initial = readStoredLocale();
    applyDocumentLocale(initial);
    return initial;
  });

  useEffect(() => {
    applyDocumentLocale(uiLocale);
  }, [uiLocale]);

  const setUiLocale = useCallback((locale: UiLocale) => {
    setUiLocaleState(locale);
    try {
      window.localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* ignore */
    }
    applyDocumentLocale(locale);
  }, []);

  const value = useMemo(
    () => ({ uiLocale, setUiLocale }),
    [uiLocale, setUiLocale]
  );

  return (
    <UiLocaleContext.Provider value={value}>{children}</UiLocaleContext.Provider>
  );
};

export function useUiLocale(): UiLocaleContextValue {
  const ctx = useContext(UiLocaleContext);
  if (!ctx) {
    throw new Error("useUiLocale must be used within UiLocaleProvider");
  }
  return ctx;
}
